import { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useDossierStore } from "../store/dossierStore";

/**
 * Session Supabase partagée (user + chargement initial).
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const finish = (u: User | null) => {
      if (!mounted) return;
      setUser(u);
      setLoading(false);
    };

    void (async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (!mounted) return;
      if (error) {
        console.warn("[auth] getSession:", error.message);
        // Ne pas appeler signOut() ici : erreurs transitoires / réseau déclenchaient un « logout flash ».
        // Nettoyage explicite réservé à l’UI (déconnexion utilisateur).
        finish(null);
        return;
      }
      finish(session?.user ?? null);
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      // Ne jamais traiter `!session` comme SIGNED_OUT : beaucoup d’événements passent
      // transitoirement sans session et provoquent un « logout flash » côté UI.
      if (event === "SIGNED_OUT") {
        useDossierStore.getState().clearDossier();
        finish(null);
        return;
      }
      if (session?.user) {
        finish(session.user);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return { user, loading };
}
